/**
 * Exempt customer (piece 25; decision 1781): the four steps that take a site
 * from no certificate to one that exempts its sales, each read from the site's
 * `CertificateRequest` and `TaxExemptCertificate` rows. 🔒 Nothing here is
 * stored; the tax controller answers the facts and this function reads them.
 */
import type { SiteExemptionFormRow } from './register';

export const EXEMPT_CUSTOMER_STEPS = ['findForm', 'requestOrRecord', 'checkFields', 'confirm'] as const;
export type ExemptCustomerStep = (typeof EXEMPT_CUSTOMER_STEPS)[number];

/** The facts read for one site; a browser fetch path, relative to the API root. */
export const exemptCustomerPath = (siteId: string): string => `/tax/sites/${encodeURIComponent(siteId)}/exemption-steps`;

/** The certificate the steps read: the site's newest statewide certificate for its state that has not ended. */
export interface ExemptCertificateFacts {
  id: string;
  formType: string | null;
  exemptionReason: string;
  effectiveFrom: string;
  /** The certificate's own file; a certificate always names one, and the file holds the expiry. */
  fileId: string;
  reviewedAt: string | null;
}

/** 🔒 The facts behind the four steps, each read from a row and none of them stored (decision 1781). */
export interface ExemptCustomerView {
  siteId: string;
  /** The state the site's address names; empty where it names none, and then no step can be taken. */
  stateCode: string | null;
  stateName: string | null;
  /** The state's own exemption form, with the forms library's unhidden copies; empty for a state with none listed. */
  form: SiteExemptionFormRow | null;
  /** How many of the site's certificate requests are open: sent, unused, unrevoked and unexpired. */
  openRequests: number;
  certificate: ExemptCertificateFacts | null;
}

export interface ExemptCustomerStepFacts {
  done: boolean;
  /** Whether what the step needs is done; a step that cannot be reached draws as later. */
  reachable: boolean;
}

/**
 * 🔒 Each step's done test (decision 1781). Reading the form is stored
 * nowhere, so Find the form is done once a request is open or a certificate
 * stands. Request or record is done the same way; Check the read fields once
 * the certificate names its form type, its reason, its start and its file;
 * Confirm once a person has reviewed it. A key the answer lacks reads as not
 * done.
 */
export function exemptCustomerSteps(view: ExemptCustomerView): Record<ExemptCustomerStep, ExemptCustomerStepFacts> {
  const located = Boolean(view.stateCode);
  const certificate = view.certificate ?? null;
  const asked = (view.openRequests ?? 0) > 0 || certificate !== null;
  const fieldsRead = certificate !== null && Boolean(certificate.formType?.trim()) && Boolean(certificate.exemptionReason?.trim()) && Boolean(certificate.effectiveFrom) && Boolean(certificate.fileId);
  return {
    findForm: { done: located && asked, reachable: located },
    requestOrRecord: { done: located && asked, reachable: located },
    checkFields: { done: fieldsRead, reachable: certificate !== null },
    confirm: { done: certificate !== null && certificate.reviewedAt !== null, reachable: certificate !== null },
  };
}
